import React, { useState } from "react";

import { useTranslation } from "react-i18next";

import { useDriversTableStore } from "@/stores/useTableStore";

import { DriverDataDto } from "../../../../../../Pages/Drivers/interfaces";
import useAxiosPrivate from "../../../../../../api/hooks/useAxiosPrivate";
import {
  DRIVERS_ACTIONS,
  GeneralDialogProps,
  ROUTES,
  useMediaQueries,
} from "../../../../../../shared";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import {
  capitalizeResourceFirstLetter,
  refreshTableAndUpdateDetails,
} from "../../../../../../shared/functions/functions";
import useResponse from "../../../../../../shared/hooks/useResponse";
import useTableItemDetails from "../../../../../DetailsSection/hooks/useTableItemDetails";
import Dialog from "../../../../../Dialog/Dialog";
import DialogTitleWithTooltip from "../../../../../Dialog/components/DialogTitleWithTooltip/DialogTitleWithTooltip";
import { DialogWidth } from "../../../../../Dialog/types/enums";
import { EditDriverPayloadType } from "../../../../types";

import EditDriverDialogContent from "./EditDriverDialogContent";

interface IEditDriverDialogProps {
  rowData: DriverDataDto | null;
}

const EditDriverDialog = ({
  isOpen,
  onClose,
  rowData,
}: IEditDriverDialogProps & GeneralDialogProps) => {
  const { t } = useTranslation();
  const { toMd } = useMediaQueries();
  const { putData } = useAxiosPrivate();
  const { getTableItemDetails } = useTableItemDetails();
  const { handleCommonError, handleResponse } = useResponse();

  const {
    detailsData,
    params: { contains },
  } = useDriversTableStore();

  const resource = ROUTES.Drivers;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isReadyToConfirm, setIsReadyToConfirm] = useState<boolean | "" | null>(
    false,
  );
  const [payload, setPayload] = useState<EditDriverPayloadType>({
    id: null,
    cardNumber: null,
    cardName: null,
    comment: null,
  });

  const isReadyToCallApi = isReadyToConfirm && resource;

  const editableItemHasOpenedDetails =
    detailsData && detailsData.id === payload.id;

  const resourceString =
    resource && capitalizeResourceFirstLetter(resource).slice(0, -1);

  const valueInSearchField = contains;
  const successMessage = t(`Alert##successfully edited`, { resourceString });

  const handleCloseEditDriverDialog = () => {
    onClose(DRIVERS_ACTIONS.EditDriver);
  };

  const handleSubmit = () => {
    if (isReadyToCallApi) {
      const editDriver = async () => {
        try {
          setIsLoading(true);
          await putData(resource, payload);

          // refresh table and set updated details if details section is opened

          refreshTableAndUpdateDetails({
            valueInSearchField,
            resourceId: payload.id,
            successMessage,
            itemHasOpenedDetails: editableItemHasOpenedDetails,
            getTableItemDetails,
            handleResponse,
          });
          handleCloseEditDriverDialog();
        } catch (error) {
          handleCommonError(error, t);
        } finally {
          setIsLoading(false);
        }
      };

      editDriver();
    }
  };

  return (
    <Dialog
      data-testid="edit-driver-dialog"
      title={
        rowData &&
        ((
          <DialogTitleWithTooltip
            title={t("Dialog##edit driver", {
              name: rowData.cardName,
            })}
            tooltipText={rowData.cardName}
          />
        ) as any)
      }
      isTitleSeparator
      open={isOpen}
      close={handleCloseEditDriverDialog}
      submit={handleSubmit}
      width={!toMd ? DialogWidth.MinWidth : undefined}
      actions={
        <DialogActions
          onClose={handleCloseEditDriverDialog}
          isLoading={isLoading}
          isreadyToConfirm={isReadyToConfirm}
          confirmationButtonText={t("Button##save")}
          confirmationButtonTestId="edit-driver-button"
          cancelButtonText={t("Button##close")}
          cancelButtonTestId="close-edit-driver"
        />
      }
    >
      {rowData && (
        <EditDriverDialogContent
          rowData={rowData}
          setPayload={setPayload}
          setIsReadyToConfirm={setIsReadyToConfirm}
          isLoading={isLoading}
        />
      )}
    </Dialog>
  );
};

export default React.memo(EditDriverDialog);
